const { logWarn } = require('../logger');

const DEFAULT_VIDEO_AUCTION_TIMEOUT_MS = 1500;

function parseCustParams(rawValue) {
  if (typeof rawValue !== 'string' || !rawValue.trim()) {
    return {};
  }

  return rawValue
    .split('&')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .reduce((accumulator, pair) => {
      const separatorIndex = pair.indexOf('=');
      if (separatorIndex <= 0) {
        return accumulator;
      }

      let key = pair.slice(0, separatorIndex);
      let value = pair.slice(separatorIndex + 1);
      try {
        key = decodeURIComponent(key);
      } catch (_) {
        // leave as-is when decoding fails
      }
      try {
        value = decodeURIComponent(value);
      } catch (_) {
        // leave as-is when decoding fails
      }
      if (key) {
        accumulator[key] = value;
      }
      return accumulator;
    }, {});
}

function stringifyCustParams(params) {
  return Object.entries(params)
    .filter(([key, value]) => key && value !== undefined && value !== null && String(value).trim())
    .map(([key, value]) => `${key}=${String(value).trim()}`)
    .join('&');
}

function mergeTargetingIntoGptTag(adTagUrl, targeting) {
  if (!adTagUrl || !targeting || Object.keys(targeting).length === 0) {
    return adTagUrl;
  }

  try {
    const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://www.narcity.com';
    const url = new URL(adTagUrl, baseOrigin);
    const currentParams = parseCustParams(url.searchParams.get('cust_params') || '');
    const nextParams = { ...currentParams };

    Object.entries(targeting).forEach(([key, value]) => {
      if (!key || value === undefined || value === null) {
        return;
      }
      const normalized = (Array.isArray(value) ? value : [value])
        .map((item) => String(item).trim())
        .filter(Boolean)
        .join(',');
      if (!normalized) {
        return;
      }
      nextParams[key] = normalized;
    });

    const serialized = stringifyCustParams(nextParams);
    if (serialized) {
      url.searchParams.set('cust_params', serialized);
    }

    const correlator = url.searchParams.get('correlator');
    if (!correlator) {
      url.searchParams.set('correlator', String(Date.now()));
    }

    return url.toString();
  } catch (err) {
    logWarn('Failed to merge Prebid targeting into stories ad tag', err);
    return adTagUrl;
  }
}

function getBidsForAdUnit(pbjs, adUnitCode) {
  if (!pbjs || !adUnitCode) {
    return [];
  }

  if (typeof pbjs.getBidResponsesForAdUnitCode === 'function') {
    const response = pbjs.getBidResponsesForAdUnitCode(adUnitCode);
    return Array.isArray(response?.bids) ? response.bids : [];
  }

  if (typeof pbjs.getBidResponses === 'function') {
    const allResponses = pbjs.getBidResponses() || {};
    const response = allResponses[adUnitCode];
    return Array.isArray(response?.bids) ? response.bids : [];
  }

  return [];
}

function getAdserverTargeting(pbjs, adUnitCode) {
  if (!pbjs || !adUnitCode) {
    return null;
  }

  if (typeof pbjs.getAdserverTargetingForAdUnitCode === 'function') {
    const targeting = pbjs.getAdserverTargetingForAdUnitCode(adUnitCode) || {};
    if (targeting && Object.keys(targeting).length > 0) {
      return targeting;
    }
  }

  if (typeof pbjs.getAdserverTargeting === 'function') {
    const targetingMap = pbjs.getAdserverTargeting() || {};
    const targeting = targetingMap[adUnitCode];
    if (targeting && Object.keys(targeting).length > 0) {
      return targeting;
    }
  }

  return null;
}

function pickWinningVideoBid(bids = []) {
  if (!Array.isArray(bids) || bids.length === 0) {
    return null;
  }

  const sorted = [...bids]
    .filter(Boolean)
    .sort((a, b) => (Number(b?.cpm) || 0) - (Number(a?.cpm) || 0));

  if (sorted.length === 0) {
    return null;
  }

  const videoBid = sorted.find((bid) => {
    return bid?.mediaType === 'video' || Boolean(bid?.vastUrl) || Boolean(bid?.vastXml);
  });

  return videoBid || sorted[0];
}

const VIDEO_PRICE_BUCKET_MAX = 20;

/**
 * The GAM stories video line items exist only at $0.10 buckets (prebid
 * "medium" granularity, $0.10..$20.00), while standard prebid targeting
 * carries penny-granular pbHg values that almost never match a video line
 * item. Quantize hb_pb to the dime grid so video bids stay eligible.
 */
function toVideoPriceBucket(value) {
  const price = Number(value);
  if (!Number.isFinite(price) || price < 0) {
    return null;
  }
  const capped = Math.min(price, VIDEO_PRICE_BUCKET_MAX);
  const dimes = Math.floor(Math.round(capped * 100) / 10);
  return (dimes / 10).toFixed(2);
}

function buildTargetingFromBid(bid) {
  if (!bid) {
    return null;
  }

  const targeting = {
    hb_bidder: bid.bidderCode || bid.bidder,
    hb_adid: bid.adId,
    hb_pb: bid.pbMg
      || toVideoPriceBucket(bid.pbHg !== undefined && bid.pbHg !== null ? bid.pbHg : bid.cpm)
      || '',
    hb_size: bid.size || (
      Number.isFinite(bid.width) && Number.isFinite(bid.height)
        ? `${bid.width}x${bid.height}`
        : ''
    ),
    hb_format: bid.mediaType || 'video'
  };

  Object.keys(targeting).forEach((key) => {
    const value = targeting[key];
    if (value === undefined || value === null || !String(value).trim()) {
      delete targeting[key];
    }
  });

  return Object.keys(targeting).length ? targeting : null;
}

function cleanupAdUnit(pbjs, adUnitCode) {
  if (!pbjs || !adUnitCode || typeof pbjs.removeAdUnit !== 'function') {
    return;
  }
  try {
    pbjs.removeAdUnit(adUnitCode);
  } catch (err) {
    logWarn('Failed to cleanup temporary stories video ad unit', err);
  }
}

function requestVideoTargeting(pbjs, adUnit, timeoutMs) {
  if (
    !pbjs
    || !adUnit
    || !adUnit.code
    || typeof pbjs.requestBids !== 'function'
    || typeof pbjs.addAdUnits !== 'function'
  ) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const adUnitCode = adUnit.code;
    let completed = false;

    const complete = (targeting) => {
      if (completed) {
        return;
      }
      completed = true;
      cleanupAdUnit(pbjs, adUnitCode);
      resolve(targeting || null);
    };

    const safeTimeout = Number.isFinite(timeoutMs) && timeoutMs > 0
      ? timeoutMs
      : DEFAULT_VIDEO_AUCTION_TIMEOUT_MS;

    const timeoutId = setTimeout(() => complete(null), safeTimeout + 100);

    try {
      pbjs.addAdUnits([adUnit]);
    } catch (err) {
      clearTimeout(timeoutId);
      logWarn('Failed to register stories video ad unit for Prebid auction', err);
      complete(null);
      return;
    }

    try {
      pbjs.requestBids({
        timeout: safeTimeout,
        adUnitCodes: [adUnitCode],
        bidsBackHandler: function onBidsBack() {
          clearTimeout(timeoutId);

          const targeting = getAdserverTargeting(pbjs, adUnitCode);
          if (targeting && Object.keys(targeting).length > 0) {
            const normalizedTargeting = { ...targeting };
            if (!normalizedTargeting.hb_format) {
              normalizedTargeting.hb_format = 'video';
            }
            const videoBucket = toVideoPriceBucket(normalizedTargeting.hb_pb);
            if (videoBucket) {
              normalizedTargeting.hb_pb = videoBucket;
            }
            complete(normalizedTargeting);
            return;
          }

          const bidResponses = getBidsForAdUnit(pbjs, adUnitCode);
          const winningBid = pickWinningVideoBid(bidResponses);
          complete(buildTargetingFromBid(winningBid));
        }
      });
    } catch (err) {
      clearTimeout(timeoutId);
      logWarn('Stories Prebid video request failed', err);
      complete(null);
    }
  });
}

function normalizeStoriesPlacement(adState) {
  return adState === 'interstitial' ? 'interstitial' : 'preroll';
}

function createStoriesPrebidAdTagResolver(options = {}) {
  const pbjs = options.pbjs;
  const createAdUnit = typeof options.createAdUnit === 'function' ? options.createAdUnit : null;
  const baseTargeting = options.baseTargeting && typeof options.baseTargeting === 'object'
    ? options.baseTargeting
    : null;
  const timeoutMs = Number.isFinite(options.timeoutMs) && options.timeoutMs > 0
    ? options.timeoutMs
    : DEFAULT_VIDEO_AUCTION_TIMEOUT_MS;
  const inFlightByPlacement = new Map();

  return async function resolveStoriesAdTag({ tagUrl, adState }) {
    if (!tagUrl) {
      return tagUrl;
    }

    const tagWithBaseTargeting = baseTargeting
      ? mergeTargetingIntoGptTag(tagUrl, baseTargeting)
      : tagUrl;

    if (!pbjs || !createAdUnit) {
      return tagWithBaseTargeting;
    }

    const placement = normalizeStoriesPlacement(adState);

    let targetingPromise = inFlightByPlacement.get(placement);
    if (!targetingPromise) {
      let adUnit;
      try {
        adUnit = createAdUnit({ placement, adState });
      } catch (err) {
        logWarn('Stories video ad-unit factory threw; skipping Prebid auction', err);
        return tagWithBaseTargeting;
      }

      if (!adUnit || !adUnit.code || !Array.isArray(adUnit.bids) || adUnit.bids.length === 0) {
        return tagWithBaseTargeting;
      }

      targetingPromise = requestVideoTargeting(pbjs, adUnit, timeoutMs);
      inFlightByPlacement.set(placement, targetingPromise);
      targetingPromise.finally(() => {
        if (inFlightByPlacement.get(placement) === targetingPromise) {
          inFlightByPlacement.delete(placement);
        }
      });
    }

    const targeting = await targetingPromise;
    if (!targeting) {
      return tagWithBaseTargeting;
    }

    return mergeTargetingIntoGptTag(tagWithBaseTargeting, targeting);
  };
}

module.exports = {
  createStoriesPrebidAdTagResolver,
  mergeTargetingIntoGptTag
};
